import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import Tabs from '../../components/Tabs';
import ProfileViewer from './ProfileViewer';
import CalendarWithTabs from '../Calendar/CalendarWithTabs';
import ChatConversation from '../Chats/Chat/ChatConversation';
import useCalendarDataPredictedOtherUser from '../../hooks/useCalendarDataPredictedOtherUser';
import { yearMonthString, isCalendarShared, getInitialDateRange, findChatId, determineAvailableTabs } from '../../data/functions';

const TAG = '[ProfileFull]';

// Content component for profile viewing - shows tabs for chat, profile, calendar
function ProfileFull({ id, initialTab }) {
  // Get current user data for share checks
  const currentUserId = useSelector((s) => s.user.authUserId);
  const currentUserProfile = useSelector((s) => s.user.profile);
  const chats = useSelector((s) => s.chats.chats);
  // Get share cache to check calendar access
  const receivedShares = useSelector((s) => s.calendarShares.receivedShares);

  // Find chat between current user and viewed profile
  const chatId = findChatId(chats, currentUserId, id);

  // Track active tab
  const [tab, setTab] = useState(initialTab || 'profile');
  // Track which tabs to show based on user role
  const [availableTabs, setAvailableTabs] = useState(
    determineAvailableTabs(currentUserProfile?.role, receivedShares, id, currentUserId)
  );

  // Update tabs when role or share status changes
  useEffect(() => {
    setAvailableTabs(determineAvailableTabs(currentUserProfile?.role, receivedShares, id, currentUserId));
  }, [currentUserProfile?.role, receivedShares, id, currentUserId]);

  // Track date range locally (modal is temporary)
  const { startMonth: initialStartMonth, endMonth: initialEndMonth } = getInitialDateRange();
  const [startMonth, setStartMonth] = useState(initialStartMonth);
  const [endMonth, setEndMonth] = useState(initialEndMonth);

  // Generate a random calendar ID for this instance
  const calendarId = useMemo(() => Math.random().toString(36).substring(2, 15), []);
  
  // Fetch calendar data for viewed user
  const { calendarData, loading: calendarLoading } = useCalendarDataPredictedOtherUser(id, startMonth, endMonth);

  // Set initial tab
  useEffect(() => {
    if (initialTab && initialTab !== tab) {
      setTab(initialTab);
    }
  }, [initialTab]);

  // Stable month change handlers prevent observer resets
  const handleStartMonthChange = useCallback((newStart) => {
    setStartMonth(newStart);
  }, []);
  const handleEndMonthChange = useCallback((newEnd) => {
    setEndMonth(newEnd);
  }, []);

  return (
    <div className="flex flex-col h-full">
      {/* Tab selector - always below topbar */}
      <Tabs
        tabs={availableTabs}
        selectedTab={tab}
        onTabSet={setTab}
      />

      {/* Content area - fills remaining space */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {/* Profile tab */}
        {tab === 'profile' && (
          <div className="h-full overflow-y-auto scrollbar-hide">
            <ProfileViewer userId={id} />
          </div>
        )}

        {/* Chat tab */}
        {tab === 'chat' && (
          <ChatConversation chatId={chatId} onSetTab={setTab} />
        )}

        {/* Calendar tab */}
        {tab === 'calendar' && (
          <div className="h-full overflow-y-auto scrollbar-hide">
            <CalendarWithTabs
              calendarData={calendarData}
              startMonth={startMonth}
              endMonth={endMonth}
              onStartMonthChange={handleStartMonthChange}
              onEndMonthChange={handleEndMonthChange}
              userId={id}
              isLoading={calendarLoading}
              calendarId={calendarId}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default ProfileFull;
